/**
 * Frontend configuration for retailer, client, and channel selections.
 *
 * This mirrors backend/config/retailer_config.py structure for consistency.
 * In production, this should be hydrated from /api/config/retailers, but
 * cached startup config is used as a safe local fallback.
 */

import type { CampaignReportType } from "../types";

export type UploadReportType =
  | CampaignReportType
  | "social"
  | "linear"
  | "streaming"
  | "hess";

export interface ChannelFileRole {
  id: string;
  name: string;
  fileFormats: string[];
  description: string;
}

export interface ChannelOption {
  id: string;
  name: string;
  fileFormats: string[];
  description: string;
  uploadEndpoint?: string;
  reportType?: UploadReportType;
  normalizerKey?: string | null;
  fileCount?: number | null;
  minFileCount?: number;
  maxFileCount?: number;
  fileRoles?: ChannelFileRole[];
  processor?: string;
  required?: boolean;
}

export interface RetailerOption {
  id: string;
  name: string;
  channels: ChannelOption[];
}

export interface ClientOption {
  id: string;
  name: string;
  logoFile: string;
  logoDarkMode?: string;
}

export interface ChannelUploadDescriptor {
  id: string;
  name: string;
  fileFormats: string[];
  description: string;
  uploadEndpoint: string;
  reportType: UploadReportType;
  normalizerKey?: string;
  fileCount: number | null;
  minFileCount: number;
  maxFileCount: number;
  fileRoles: ChannelFileRole[];
  processor: string;
  required: boolean;
  uploadMode: UploadUploadMode;
}

export type UploadUploadMode = "campaign" | "linear" | "streaming" | "hess";

export type ApiChannelConfig = {
  id: string;
  name: string;
  file_formats: string[];
  normalizer_key?: string | null;
  description: string;
  report_type: UploadReportType;
  upload_endpoint: string;
  processor?: string;
  file_count?: number | null;
  min_file_count?: number;
  max_file_count?: number;
  file_roles?: ChannelFileRole[];
  required?: boolean;
};

export type ApiRetailerConfig = {
  id: string;
  name: string;
  logo_file?: string | null;
  channels: ApiChannelConfig[];
};

export type ApiClientConfig = {
  id: string;
  name: string;
  logo_file: string;
  logo_dark_mode?: string | null;
};

export const DEFAULT_UPLOAD_ENDPOINT = "/api/upload/report";
export const LINEAR_UPLOAD_ENDPOINT = "/api/upload/linear";
export const STREAMING_UPLOAD_ENDPOINT = "/api/upload/streaming";
export const HESS_UPLOAD_ENDPOINT = "/api/upload/hess";
const DEFAULT_FILE_COUNT: number | null = null;
const DEFAULT_MIN_FILE_COUNT = 1;
const DEFAULT_MAX_FILE_COUNT = 1;
const DEFAULT_FILE_ROLES: ChannelFileRole[] = [];

function inferUploadMode(uploadEndpoint: string): UploadUploadMode {
  if (uploadEndpoint === LINEAR_UPLOAD_ENDPOINT) {
    return "linear";
  }

  if (uploadEndpoint === STREAMING_UPLOAD_ENDPOINT) {
    return "streaming";
  }

  if (uploadEndpoint === HESS_UPLOAD_ENDPOINT) {
    return "hess";
  }

  return "campaign";
}

function inferReportType(channelId: string): ChannelUploadDescriptor["reportType"] {
  if (channelId === "social") {
    return "social";
  }

  if (channelId === "onsite") {
    return "onsite";
  }

  if (channelId === "offsite") {
    return "offsite";
  }

  if (channelId === "streaming") {
    return "streaming";
  }

  if (channelId === "linear") {
    return "linear";
  }

  if (channelId === "hess_search") {
    return "hess";
  }

  return "search";
}

function normalizeUploadDescriptor(
  channel: ChannelOption,
): ChannelUploadDescriptor {
  const endpoint = channel.uploadEndpoint || DEFAULT_UPLOAD_ENDPOINT;
  const fileCount =
    channel.fileCount === undefined ? DEFAULT_FILE_COUNT : channel.fileCount;
  const minFileCount = channel.minFileCount ?? DEFAULT_MIN_FILE_COUNT;
  const maxFileCount = channel.maxFileCount ?? DEFAULT_MAX_FILE_COUNT;

  return {
    id: channel.id,
    name: channel.name,
    fileFormats: [...channel.fileFormats],
    description: channel.description,
    uploadEndpoint: endpoint,
    normalizerKey: channel.normalizerKey ?? undefined,
    reportType: channel.reportType || inferReportType(channel.id),
    fileCount,
    minFileCount: minFileCount,
    maxFileCount: maxFileCount,
    fileRoles: channel.fileRoles ? [...channel.fileRoles] : [...DEFAULT_FILE_ROLES],
    processor: channel.processor || "retailer_normalizer_factory",
    required: channel.required ?? true,
    uploadMode: inferUploadMode(endpoint),
  };
}

function normalizeApiChannel(channel: ApiChannelConfig): ChannelOption {
  return {
    id: channel.id,
    name: channel.name,
    fileFormats: channel.file_formats,
    description: channel.description,
    uploadEndpoint: channel.upload_endpoint,
    normalizerKey: channel.normalizer_key,
    reportType: channel.report_type,
    fileCount: channel.file_count,
    minFileCount: channel.min_file_count,
    maxFileCount: channel.max_file_count,
    fileRoles: channel.file_roles
      ? [...channel.file_roles]
      : [...DEFAULT_FILE_ROLES],
    processor: channel.processor,
    required: channel.required,
  };
}

export function adaptApiRetailers(payload: ApiRetailerConfig[]): RetailerOption[] {
  return payload.map((retailer) => ({
    id: retailer.id,
    name: retailer.name,
    channels: retailer.channels.map(normalizeApiChannel),
  }));
}

export function adaptApiClients(payload: ApiClientConfig[]): ClientOption[] {
  return payload.map((client) => ({
    id: client.id,
    name: client.name,
    logoFile: client.logo_file,
    logoDarkMode: client.logo_dark_mode ?? undefined,
  }));
}

export function getUploadDescriptor(
  channels: ChannelOption[],
  channelId: string,
): ChannelUploadDescriptor | undefined {
  const found = channels.find((channel) => channel.id === channelId);
  return found ? normalizeUploadDescriptor(found) : undefined;
}

export function getUploadDescriptorByEndpoint(
  channels: ChannelOption[],
  endpoint: string,
): ChannelUploadDescriptor | undefined {
  const found = channels.find(
    (channel) => (channel.uploadEndpoint || DEFAULT_UPLOAD_ENDPOINT) === endpoint,
  );
  return found ? normalizeUploadDescriptor(found) : undefined;
}

export function getUploadDescriptorFromAllRetailersByEndpoint(
  endpoint: string,
): ChannelUploadDescriptor | undefined {
  return getUploadDescriptorFromRetailersByEndpoint(RETAILERS, endpoint);
}

export function getUploadDescriptorHint(descriptor: ChannelUploadDescriptor): string {
  if (descriptor.maxFileCount === descriptor.minFileCount) {
    return descriptor.maxFileCount === 1
      ? "1 file"
      : `${descriptor.maxFileCount} files`;
  }

  return `${descriptor.minFileCount}-${descriptor.maxFileCount} files`;
}

export const RETAILERS: RetailerOption[] = [
  {
    id: "walmart",
    name: "Walmart",
    channels: [
      {
        id: "search",
        name: "Search (Sponsored Products)",
        fileFormats: [".csv", ".xlsx"],
        description: "Walmart Sponsored Products reports with 14-day attribution",
        uploadEndpoint: "/api/upload/report",
        reportType: "search",
      },
      {
        id: "onsite",
        name: "Onsite Display",
        fileFormats: [".csv", ".xlsx"],
        description: "Walmart Display Ads with ROAS and CTR metrics",
        uploadEndpoint: "/api/upload/report",
        reportType: "onsite",
      },
      {
        id: "offsite",
        name: "Offsite Display",
        fileFormats: [".xlsx"],
        description: "Walmart Offsite Display - Excel only",
        uploadEndpoint: "/api/upload/report",
        reportType: "offsite",
      },
    ],
  },
  {
    id: "kroger",
    name: "Kroger",
    channels: [
      {
        id: "search",
        name: "Search",
        fileFormats: [".csv", ".xlsx"],
        description: "Kroger daily performance reports",
        uploadEndpoint: "/api/upload/report",
        reportType: "search",
      },
      {
        id: "offsite",
        name: "Offsite Display (TTD/Kokai)",
        fileFormats: [".xlsx"],
        description:
          "Kroger programmatic display via TTD - includes separate sales sheet",
        uploadEndpoint: "/api/upload/report",
        reportType: "offsite",
      },
    ],
  },
  {
    id: "albertsons",
    name: "Albertsons",
    channels: [
      {
        id: "search",
        name: "Search (Sponsored Products)",
        fileFormats: [".csv", ".xlsx"],
        description:
          "Albertsons sponsored product reports with sales, NTB, and frequency metrics",
        uploadEndpoint: "/api/upload/report",
        reportType: "search",
      },
      {
        id: "criteo",
        name: "Criteo (via Albertsons)",
        fileFormats: [".csv", ".xlsx"],
        description: "Criteo reports for Albertsons/Safeway",
        uploadEndpoint: "/api/upload/report",
        reportType: "search",
      },
    ],
  },
  {
    id: "ttd",
    name: "The Trade Desk (TTD)",
    channels: [
      {
        id: "offsite",
        name: "Programmatic Display & Video",
        fileFormats: [".xlsx"],
        description: "The Trade Desk offsite performance exports (Excel)",
        uploadEndpoint: "/api/upload/report",
        reportType: "offsite",
      },
      {
        id: "albertsons_offsite",
        name: "Albertsons Programmatic Display",
        fileFormats: [".xlsx"],
        description:
          "The Trade Desk exports limited to Albertsons placements (Excel)",
        uploadEndpoint: "/api/upload/report",
        reportType: "offsite",
      },
    ],
  },
  {
    id: "mntn",
    name: "MNTN",
    channels: [
      {
        id: "streaming",
        name: "Streaming TV",
        fileFormats: [".csv"],
        description: "MNTN Streaming TV with keyword, DMA, and network performance metrics",
        uploadEndpoint: "/api/upload/streaming",
        reportType: "streaming",
        normalizerKey: "mntn_streaming",
        fileCount: 4,
        minFileCount: 4,
        maxFileCount: 4,
        processor: "streaming_normalizer",
      },
    ],
  },
  {
    id: "meta",
    name: "Meta",
    channels: [
      {
        id: "social",
        name: "Social Media (Facebook/Instagram)",
        fileFormats: [".csv"],
        description:
          "Meta Ads Manager social campaigns with engagement and awareness metrics",
        uploadEndpoint: "/api/upload/report",
        reportType: "social",
        normalizerKey: "meta_social",
      },
    ],
  },
  {
    id: "hess",
    name: "Hess",
    channels: [
      {
        id: "hess_search",
        name: "Search + Total Sales (2 files)",
        fileFormats: [".csv"],
        description:
          "Upload 2 CSVs: total sales by month + daily ad-attributed product data",
        uploadEndpoint: "/api/upload/hess",
        reportType: "hess",
        normalizerKey: "hess_processor",
        fileCount: 2,
        minFileCount: 2,
        maxFileCount: 2,
        processor: "hess_processor",
      },
    ],
  },
  {
    id: "linear",
    name: "Linear TV",
    channels: [
      {
        id: "linear",
        name: "Linear TV Multi-Channel",
        fileFormats: [".xlsx", ".xls"],
        description: "Upload 1-3 Linear TV Excel files, one file per channel",
        uploadEndpoint: "/api/upload/linear",
        reportType: "linear",
        normalizerKey: "linear_normalizer",
        fileCount: null,
        minFileCount: 1,
        maxFileCount: 3,
        processor: "linear_normalizer",
      },
    ],
  },
];

export const CLIENTS: ClientOption[] = [
  {
    id: "ajinomoto",
    name: "Ajinomoto",
    logoFile: "ajinomoto-logo.png",
    logoDarkMode: undefined,
  },
  // Add new clients here:
  // {
  //     id: "client_b",
  //     name: "Client B",
  //     logoFile: "client-b-logo.png",
  // },
];

export function getRetailerById(
  retailerId: string,
): RetailerOption | undefined {
  return getRetailerByIdFrom(RETAILERS, retailerId);
}

export function getClientById(clientId: string): ClientOption | undefined {
  return CLIENTS.find((c) => c.id.toLowerCase() === clientId.toLowerCase());
}

export function getRetailerByIdFrom(
  retailers: RetailerOption[],
  retailerId: string,
): RetailerOption | undefined {
  return retailers.find(
    (retailer) => retailer.id.toLowerCase() === retailerId.toLowerCase(),
  );
}

export function getChannelsForRetailer(retailerId: string): ChannelOption[] {
  return getChannelsForRetailerFrom(RETAILERS, retailerId);
}

export function getChannelsForRetailerFrom(
  retailers: RetailerOption[],
  retailerId: string,
): ChannelOption[] {
  return getRetailerByIdFrom(retailers, retailerId)?.channels ?? [];
}

export function getChannelConfig(
  retailerId: string,
  channelId: string,
): ChannelOption | undefined {
  return getChannelConfigFrom(RETAILERS, retailerId, channelId);
}

export function getChannelConfigFrom(
  retailers: RetailerOption[],
  retailerId: string,
  channelId: string,
): ChannelOption | undefined {
  const retailer = getRetailerByIdFrom(retailers, retailerId);
  if (!retailer) {
    return undefined;
  }

  return retailer.channels.find(
    (channel) => channel.id.toLowerCase() === channelId.toLowerCase(),
  );
}

export function getUploadDescriptorForChannel(
  retailerId: string,
  channelId: string,
): ChannelUploadDescriptor | undefined {
  return getUploadDescriptorForChannelFrom(RETAILERS, retailerId, channelId);
}

export function getUploadDescriptorForChannelFrom(
  retailers: RetailerOption[],
  retailerId: string,
  channelId: string,
): ChannelUploadDescriptor | undefined {
  return getUploadDescriptor(
    getChannelsForRetailerFrom(retailers, retailerId),
    channelId,
  );
}

export function getUploadDescriptorFromRetailersByEndpoint(
  retailers: RetailerOption[],
  endpoint: string,
): ChannelUploadDescriptor | undefined {
  for (const retailer of retailers) {
    const descriptor = getUploadDescriptorByEndpoint(retailer.channels, endpoint);
    if (descriptor) {
      return descriptor;
    }
  }
  return undefined;
}

export function getChannelConfigUploadDescriptor(
  retailerId: string,
  channelId: string,
): ChannelUploadDescriptor | undefined {
  return getUploadDescriptorForChannel(retailerId, channelId);
}

export function validateFileFormat(
  retailerId: string,
  channelId: string,
  filename: string,
): boolean {
  const descriptor = getUploadDescriptorForChannel(retailerId, channelId);
  if (!descriptor) {
    return false;
  }

  const extension = filename.substring(filename.lastIndexOf(".")).toLowerCase();
  return descriptor.fileFormats.includes(extension);
}
